import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Row, Space, Table, message } from 'antd'
import { type FilterValue } from 'antd/es/table/interface'
import { useMemo, type FC } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { client } from '~/api/base'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { type CouponCode } from '~/types/coupon-codes'
import {
  clipboard,
  compareDate,
  formatDateTime,
  showErrors
} from '~/utils/helpers'
import { useUpcomingCoursesQuery } from '../Courses/query'

interface Props {
  onEdit: (item: CouponCode) => void
  searchValue: string | null
  onFilter: (filters: any) => void
}

const CouponList: FC<Props> = ({ searchValue, onEdit, onFilter }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['coupons'],
    queryFn: async () => {
      const res = await client.get<{ data: any[] }>('/api/coupon-codes')

      return res.data
    }
  })
  const source = useTableDataSourceKey(data?.data)
  const filtered = useMemo(() => {
    if (searchValue == null) {
      return source
    }
    return source.filter((item: CouponCode) =>
      `${item.code}`.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [searchValue, source])
  const coursesQuery = useUpcomingCoursesQuery()
  const queryClient = useQueryClient()
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await client.delete(`api/coupon-codes/${id}`)
    },
    onError: showErrors,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] })
      message.success('Deleted successfully')
    }
  })

  return (
    <Table
      size='small'
      tableLayout='fixed'
      dataSource={filtered}
      loading={isFetching}
      pagination={{ pageSize: 20 }}
      footer={() => (
        <Row justify='start'>
          <span>Total: {data?.data.length} codes</span>
        </Row>
      )}
      onChange={(_, filters: Record<string, FilterValue | null>) => {
        onFilter(filters)
      }}
    >
      <Table.Column
        title='Event'
        dataIndex='event_id'
        render={(_, record: CouponCode) => record.course?.name}
        filters={coursesQuery.data?.map((i) => ({ text: i.name, value: i.id }))}
        onFilter={(value, record) => record.event_id === value}
      />
      <Table.Column
        title='Code'
        dataIndex='code'
        render={(value: string) => (
          <Space size='small'>
            <span>{value}</span>
            <Button
              size='small'
              icon={<CopyOutlined />}
              onClick={() => {
                clipboard(value)
                message.success('Copied')
              }}
            />
          </Space>
        )}
      />
      <Table.Column title='Description' dataIndex='description' />
      <Table.Column title='Discount type' dataIndex='discount_type' />
      <Table.Column
        title='Discount value'
        dataIndex='value'
        render={(_, record: CouponCode) => {
          if (record.discount_type === 'fixed') {
            return `$${record.value}`
          }

          return `${record.value} %`
        }}
      />
      <Table.Column title='Total uses' dataIndex='usages_count' />
      <Table.Column title='Usage limit' dataIndex='usage_limit' />
      <Table.Column
        title='Status'
        dataIndex='activated'
        render={(value: boolean) => (value ? 'Activated' : 'Inactivated')}
        filterMultiple={false}
        filters={[
          { text: 'Yes', value: true },
          { text: 'No', value: false }
        ]}
        onFilter={(value, record: CouponCode) => record.activated === value}
      />
      <Table.Column
        title='Expiry date'
        dataIndex='expiry_date'
        render={formatDateTime}
        sorter={(a: CouponCode, b: CouponCode) => {
          return compareDate(a.created_at, b.created_at)
        }}
      />
      <Table.Column
        title='Actions'
        align='center'
        render={(_, record: CouponCode) => (
          <Space size='middle'>
            <Button
              key={1}
              type='primary'
              icon={<EditOutlined />}
              onClick={() => {
                onEdit(record)
              }}
            />
            <Popconfirm
              title={`Do you want to delete the code ${record.code}?`}
              onConfirm={() => {
                deleteMutation.mutate(record.id, {
                  onSuccess: () => {
                    message.success('Deleted successfully')
                    void queryClient.invalidateQueries({ queryKey: ['coupons'] })
                  }
                })
              }}
              okText='YES'
            >
              <Button danger type='primary' icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )}
      />
    </Table>
  )
}

export default CouponList
